/**
 * V2 Paywall Footer Section Configuration
 *
 * V2 version of paywallFooterSectionConfig.ts with V2 data paths.
 * All footer elements live in content.paywall.* (NOT in shared actionBar).
 *
 * V2 path mapping:
 * - V1 footer.purchase → V2 content.paywall.purchaseAction
 * - V1 footer.autoRenewLabel → V2 content.paywall.autoRenewLabel
 * - V1 footer.bottomContainer.restore → V2 content.paywall.legalLinks.restore
 * - V1 footer.bottomContainer.termsOfUse → V2 content.paywall.legalLinks.termsOfUse
 * - V1 footer.bottomContainer.privacyPolicy → V2 content.paywall.legalLinks.privacyPolicy
 * - V1 footer.styles → V2 content.paywall.footerAppearance
 * - V1 l10n → V2 translations
 * - V1 styles → V2 appearance
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import {
  createTextStyleFields,
  createButtonContainerStyleFields,
  createV2ButtonAppearanceFields,
  createV2IconFields,
  V2_TEXT_ALIGN_OPTIONS,
} from './atomicFields';
import { createV2ButtonStyleGroups } from './v2ButtonStyleGroups';
import { PAYWALL_FOOTER_ELEMENTS_ORDER_OPTIONS } from './atomicFields/options';

/**
 * Remap V1 text style fields to V2 TypographyStyle:
 *   .styles.* → .appearance.*
 *   .appearance.textAlign → .appearance.align
 *   textAlign options: left/right → start/end
 */
function remapTextFieldsForV2(fields: FieldConfig[]): FieldConfig[] {
  return fields.map(f => {
    let path = f.path.replace('.styles.', '.appearance.');
    const isAlignField = path.endsWith('.appearance.textAlign');
    if (isAlignField) {
      path = path.replace('.appearance.textAlign', '.appearance.align');
      return { ...f, path, options: [...V2_TEXT_ALIGN_OPTIONS] };
    }
    return path !== f.path ? { ...f, path } : f;
  });
}

// ============================================
// Purchase Button (V2 paths)
// ============================================

export const V2_PAYWALL_PURCHASE_BUTTON_CONFIG: FieldConfig = {
  path: 'content.paywall.purchaseAction',
  label: 'Purchase Button',
  placeholder: 'Subscribe Now',
  type: 'text',
  l10nPath: 'content.paywall.purchaseAction.content',
  defaultValue: 'Subscribe Now',
  disableBehavior: 'null',
  guideKey: 'screenedit-paywall-footer-purchase-text',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.paywall.purchaseAction.content')),
  buttonStyleFields: [
    ...createButtonContainerStyleFields('content.paywall.purchaseAction').map(f => {
      // V2 TapTarget uses nested borders.{} and dimensions.{} instead of flat fields
      let path = f.path.replace('.styles.', '.appearance.');
      if (path.endsWith('.appearance.borderWidth')) path = path.replace('.appearance.borderWidth', '.appearance.borders.width');
      else if (path.endsWith('.appearance.borderColor')) path = path.replace('.appearance.borderColor', '.appearance.borders.color');
      else if (path.endsWith('.appearance.borderRadius')) path = path.replace('.appearance.borderRadius', '.appearance.borders.radius.uniform');
      else if (path.endsWith('.appearance.height')) path = path.replace('.appearance.height', '.appearance.dimensions.height');
      else if (path.endsWith('.appearance.width')) path = path.replace('.appearance.width', '.appearance.dimensions.width');
      return { ...f, path };
    }),
    // V2-only appearance fields (gradient, shadow, opacity, animation)
    ...createV2ButtonAppearanceFields('content.paywall.purchaseAction').map(f => ({
      ...f,
      path: f.path.replace('.styles.', '.appearance.'),
    })),
    // Icon fields
    ...createV2IconFields('content.paywall.purchaseAction'),
    // Downsale: when enabled, payment cancel navigates to a downsale screen
    {
      path: 'content.paywall.purchaseAction.action.onCancelAction',
      label: 'downsale on cancel',
      type: 'select',
      options: [{ value: 'enabled', label: 'Navigate to Downsale' }],
      defaultValue: 'enabled',
      isNullable: true,
      guideKey: 'screenedit-paywall-footer-purchase-downsale',
    },
  ],
  buttonStyleGroups: createV2ButtonStyleGroups('content.paywall.purchaseAction', { includeOnCancelAction: true }),
  paddingConfig: {
    basePath: 'content.paywall.purchaseAction.layout.insets',
    label: 'Box Padding',
  },
};

// ============================================
// Auto-Renew Label (V2 paths)
// ============================================

export const V2_PAYWALL_AUTO_RENEW_LABEL_CONFIG: FieldConfig = {
  path: 'content.paywall.autoRenewLabel',
  label: 'Auto-Renew Label',
  placeholder: 'Auto-renews monthly. Cancel anytime.',
  type: 'templateText',
  l10nPath: 'content.paywall.autoRenewLabel',
  defaultValue: '',
  disableBehavior: 'null',
  guideKey: 'screenedit-paywall-footer-autorenew-text',
  styleFields: [
    ...remapTextFieldsForV2(createTextStyleFields('content.paywall.autoRenewLabel')),
    {
      path: 'content.paywall.footerAppearance.elementsOrder',
      label: 'Elements Order',
      type: 'select',
      options: [...PAYWALL_FOOTER_ELEMENTS_ORDER_OPTIONS],
      defaultValue: 'PurchaseFirst',
      isNullable: true,
      guideKey: 'screenedit-paywall-footer-elements-order',
    },
  ],
  paddingConfig: {
    basePath: 'content.paywall.autoRenewLabel.layout.insets',
    label: 'Box Padding',
  },
  templateConfig: {
    kindPath: 'content.paywall.autoRenewLabel.kind',
    labelsPath: 'content.paywall.autoRenewLabel.templateParams.labels',
    linksPath: 'content.paywall.autoRenewLabel.templateParams.links',
    countdownsPath: 'content.paywall.autoRenewLabel.templateParams.countdowns',
  },
};

// ============================================
// Legal Links (V2 paths)
// ============================================

export const V2_PAYWALL_RESTORE_CONFIG: FieldConfig = {
  path: 'content.paywall.legalLinks.restore',
  label: 'Restore',
  placeholder: 'Restore Purchases',
  type: 'templateText',
  l10nPath: 'content.paywall.legalLinks.restore',
  defaultValue: 'Restore Purchases',
  disableBehavior: 'null',
  guideKey: 'screenedit-paywall-footer-restore-text',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.paywall.legalLinks.restore')),
  templateConfig: {
    kindPath: 'content.paywall.legalLinks.restore.kind',
    labelsPath: 'content.paywall.legalLinks.restore.templateParams.labels',
    linksPath: 'content.paywall.legalLinks.restore.templateParams.links',
    countdownsPath: 'content.paywall.legalLinks.restore.templateParams.countdowns',
  },
};

export const V2_PAYWALL_TERMS_OF_USE_CONFIG: FieldConfig = {
  path: 'content.paywall.legalLinks.termsOfUse',
  label: 'Terms of Use',
  placeholder: 'Terms of Use',
  type: 'templateText',
  l10nPath: 'content.paywall.legalLinks.termsOfUse.content',
  uriPath: 'content.paywall.legalLinks.termsOfUse.uri',
  defaultValue: 'Terms of Use',
  disableBehavior: 'null',
  guideKey: 'screenedit-paywall-footer-terms-text',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.paywall.legalLinks.termsOfUse.content')),
  templateConfig: {
    kindPath: 'content.paywall.legalLinks.termsOfUse.content.kind',
    labelsPath: 'content.paywall.legalLinks.termsOfUse.content.templateParams.labels',
    linksPath: 'content.paywall.legalLinks.termsOfUse.content.templateParams.links',
    countdownsPath: 'content.paywall.legalLinks.termsOfUse.content.templateParams.countdowns',
  },
};

export const V2_PAYWALL_PRIVACY_POLICY_CONFIG: FieldConfig = {
  path: 'content.paywall.legalLinks.privacyPolicy',
  label: 'Privacy Policy',
  placeholder: 'Privacy Policy',
  type: 'templateText',
  l10nPath: 'content.paywall.legalLinks.privacyPolicy.content',
  uriPath: 'content.paywall.legalLinks.privacyPolicy.uri',
  defaultValue: 'Privacy Policy',
  disableBehavior: 'null',
  guideKey: 'screenedit-paywall-footer-privacy-text',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.paywall.legalLinks.privacyPolicy.content')),
  templateConfig: {
    kindPath: 'content.paywall.legalLinks.privacyPolicy.content.kind',
    labelsPath: 'content.paywall.legalLinks.privacyPolicy.content.templateParams.labels',
    linksPath: 'content.paywall.legalLinks.privacyPolicy.content.templateParams.links',
    countdownsPath: 'content.paywall.legalLinks.privacyPolicy.content.templateParams.countdowns',
  },
};

// ============================================
// Footer Styles (V2 paths)
// ============================================

export const V2_PAYWALL_FOOTER_STYLES_CONFIG: FieldConfig = {
  path: 'content.paywall.footerAppearance',
  label: 'Footer Styles',
  type: 'select',
  styleFields: [
    {
      path: 'content.paywall.footerAppearance.backgroundColor',
      label: 'Background',
      type: 'color',
      defaultValue: '#1a1a1a',
      isNullable: true,
      guideKey: 'screenedit-paywall-footer-background-color',
    },
  ],
};

export const V2_PAYWALL_FOOTER_BOTTOM_STYLES_CONFIG: FieldConfig = {
  path: 'content.paywall.legalLinks.layout.insets',
  label: 'Bottom Container',
  type: 'select',
  guideKey: 'screenedit-paywall-footer-legal-links-padding',
  paddingConfig: {
    basePath: 'content.paywall.legalLinks.layout.insets',
    label: 'Padding',
  },
};

// ============================================
// Exports
// ============================================

export const V2_PAYWALL_FOOTER_BUTTON_CONFIGS: FieldConfig[] = [
  V2_PAYWALL_PURCHASE_BUTTON_CONFIG,
  V2_PAYWALL_AUTO_RENEW_LABEL_CONFIG,
  V2_PAYWALL_RESTORE_CONFIG,
  V2_PAYWALL_TERMS_OF_USE_CONFIG,
  V2_PAYWALL_PRIVACY_POLICY_CONFIG,
];

export const V2_PAYWALL_FOOTER_FIELD_CONFIGS: FieldConfig[] = [
  ...V2_PAYWALL_FOOTER_BUTTON_CONFIGS,
  V2_PAYWALL_FOOTER_STYLES_CONFIG,
  V2_PAYWALL_FOOTER_BOTTOM_STYLES_CONFIG,
];

// ============================================
// Factory Functions (V2 structures)
// ============================================

/**
 * Create default V2 purchase action (TapTarget)
 */
export function createDefaultV2PurchaseAction(
  locale: string,
  text: string = 'Subscribe Now'
): Record<string, unknown> {
  return {
    content: {
      translations: { [locale]: text },
      kind: 'static',
      parameters: { labels: {}, links: {} },
      appearance: {
        fontSize: 18,
        fontFamily: 'SF Pro',
        fontWeight: 700,
        color: '#000000',
        lineHeight: 24,
        align: 'center',
      },
      layout: { insets: {} },
    },
    action: {
      kind: 'purchase',
      edges: [],
    },
    appearance: {
      backgroundColor: '#ffc201',
      borders: {
        radius: { uniform: 12 },
      },
    },
    layout: {
      insets: {
        top: 8,
        bottom: 8,
        start: 16,
        end: 16,
      },
    },
  };
}

/**
 * Create default V2 auto-renew label (LocalizedLabel)
 */
export function createDefaultV2AutoRenewLabel(
  locale: string,
  text: string = ''
): Record<string, unknown> {
  return {
    translations: { [locale]: text },
    kind: 'static',
    templateParams: { labels: {}, links: {}, countdowns: {} },
    appearance: {
      fontSize: 12,
      fontFamily: 'SF Pro',
      fontWeight: 400,
      color: '#999999',
      lineHeight: 16,
      textAlign: 'center',
    },
    layout: { insets: {} },
  };
}

/**
 * Create default V2 legal links (LegalLinks)
 */
export function createDefaultV2LegalLinks(locale: string): Record<string, unknown> {
  return {
    restore: {
      translations: { [locale]: 'Restore Purchases' },
      kind: 'static',
      templateParams: { labels: {}, links: {}, countdowns: {} },
      appearance: {
        fontSize: 12,
        fontFamily: 'SF Pro',
        fontWeight: 400,
        color: '#666666',
        lineHeight: 16,
        align: 'center',
      },
    },
    termsOfUse: {
      content: {
        translations: { [locale]: 'Terms of Use' },
        kind: 'static',
        templateParams: { labels: {}, links: {}, countdowns: {} },
        appearance: {
          fontSize: 12,
          fontFamily: 'SF Pro',
          fontWeight: 400,
          color: '#666666',
          lineHeight: 16,
          align: 'center',
        },
      },
      uri: '',
      appearance: {},
    },
    privacyPolicy: {
      content: {
        translations: { [locale]: 'Privacy Policy' },
        kind: 'static',
        templateParams: { labels: {}, links: {}, countdowns: {} },
        appearance: {
          fontSize: 12,
          fontFamily: 'SF Pro',
          fontWeight: 400,
          color: '#666666',
          lineHeight: 16,
          align: 'center',
        },
      },
      uri: '',
      appearance: {},
    },
  };
}
